import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components';
import { FiArrowLeft, FiPlus, FiFileText, FiTrash2, FiBarChart2, FiCheckSquare, FiEdit } from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';
import FolderBrowser from '../components/FolderBrowser/FolderBrowser';

const BatchTest = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);
  const [batchTests, setBatchTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [testTitle, setTestTitle] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditTest, setCurrentEditTest] = useState(null);
  
  // Folder browsing & test selection states in modal
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderTests, setFolderTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  // Define selected subjects for dropdown
  const commonSubjects = [
    'English',
    'Science',
    'Mathematics',
    'Reasoning',
    'Social Science'
  ];

  const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

  // Fetch batch details and tests
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
  
        // Fetch batch details
        const batchResponse = await axios.get(`${API_BASE_URL}/Batches/${batchId}`);
        setBatch(batchResponse.data);
  
        // Fetch tests assigned to this batch
        const batchTestsResponse = await axios.get(`${API_BASE_URL}/batch-test/batch/${batchId}`);
        setBatchTests(batchTestsResponse.data);
  
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [batchId]);
  
  // Folder change handler inside Add Test Modal
  const handleFolderChange = async (folderId) => {
    setSelectedFolderId(folderId);
    setSelectedTest('');
    setTestTitle('');
    setFocusedIndex(-1);
    
    if (folderId) {
      setTestsLoading(true);
      try {
        // Fetch tests inside the folder using optimized GET /api/Tests
        const testsRes = await axios.get(`${API_BASE_URL}/Tests?folderId=${folderId}`);
        const resData = testsRes.data;
        if (resData && Array.isArray(resData.data)) {
          setFolderTests(resData.data);
        } else if (Array.isArray(resData)) {
          // Safe fallback client-side filter if production API is not updated yet
          setFolderTests(resData.filter(t => t.folder_id === parseInt(folderId)));
        } else {
          setFolderTests([]);
        }
      } catch (err) {
        console.error('Error loading tests for folder:', err);
        setFolderTests([]);
      } finally {
        setTestsLoading(false);
      }
    } else {
      setFolderTests([]);
    }
  };

  // Keyboard navigation helper for modal selectable tests table
  const handleKeyDown = (e, items) => {
    if (items.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev < items.length - 1 ? prev + 1 : 0;
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev > 0 ? prev - 1 : items.length - 1;
        return next;
      });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < items.length) {
        const test = items[focusedIndex];
        setSelectedTest(test.test_id);
        setTestTitle(test.name);
      }
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Go back to batch view
  const handleGoBack = () => {
    navigate(`/BatchView/${batchId}`);
  };

  const [testStartTime, setTestStartTime] = useState('');
  const [testEndTime, setTestEndTime] = useState('');

  // Helper to format ISO to datetime-local input
  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const getValidityBadge = (test) => {
    if (!test.start_time && !test.end_time) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          Unrestricted
        </span>
      );
    }

    const now = new Date();
    const start = test.start_time ? new Date(test.start_time) : null;
    const end = test.end_time ? new Date(test.end_time) : null;

    if (start && now < start) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Upcoming
        </span>
      );
    } else if (end && now >= end) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Ended
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      );
    }
  };

  // Open the add test modal
  const handleOpenAddModal = () => {
    setSelectedTest('');
    setTestTitle('');
    setTestSubject('');
    setTestStartTime('');
    setTestEndTime('');
    setSelectedFolderId('');
    setFolderTests([]);
    setTestsLoading(false);
    setTypeFilter('');
    setTestSearchQuery('');
    setFocusedIndex(-1);
    setIsAddModalOpen(true);
  };

  // Close the add test modal
  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  // Open the edit test modal
  const handleOpenEditModal = (test) => {
    setCurrentEditTest(test);
    setTestTitle(test.title);
    setTestSubject(test.subject || '');
    setTestStartTime(formatForDateTimeInput(test.start_time));
    setTestEndTime(formatForDateTimeInput(test.end_time));
    setIsEditModalOpen(true);
  };

  // Close the edit test modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentEditTest(null);
  };
  
  // Assign a test to the batch
  const handleAssignTest = async () => {
    if (!selectedTest || !testTitle.trim()) {
      alert('Please select a test and provide a title');
      return;
    }

    if (testStartTime && testEndTime && new Date(testEndTime) <= new Date(testStartTime)) {
      alert('End time must be after start time');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/batch-test`, {
        batch_id: parseInt(batchId),
        test_id: parseInt(selectedTest),
        title: testTitle,
        subject: testSubject,
        start_time: testStartTime ? new Date(testStartTime).toISOString() : null,
        end_time: testEndTime ? new Date(testEndTime).toISOString() : null
      });
      
      // Add the new test to the list
      setBatchTests([...batchTests, response.data]);
      
      // Close the modal
      setIsAddModalOpen(false);
      alert('Test assigned successfully');
    } catch (error) {
      console.error('Error assigning test:', error);
      alert('Failed to assign test');
    }
  };

  // Update a test assignment
  const handleUpdateTest = async () => {
    if (!currentEditTest || !testTitle.trim()) {
      alert('Please provide a title');
      return;
    }

    if (testStartTime && testEndTime && new Date(testEndTime) <= new Date(testStartTime)) {
      alert('End time must be after start time');
      return;
    }
    
    try {
      const response = await axios.put(`${API_BASE_URL}/batch-test/${currentEditTest.id}`, {
        title: testTitle,
        subject: testSubject,
        start_time: testStartTime ? new Date(testStartTime).toISOString() : null,
        end_time: testEndTime ? new Date(testEndTime).toISOString() : null
      });
      
      // Update the test in the list
      setBatchTests(batchTests.map(test => 
        test.id === currentEditTest.id ? response.data : test
      ));
      
      // Close the modal
      setIsEditModalOpen(false);
      alert('Test updated successfully');
    } catch (error) {
      console.error('Error updating test:', error);
      alert('Failed to update test');
    }
  };

  // Delete a test assignment
  const handleDeleteTest = async (id) => {
    if (window.confirm('Are you sure you want to remove this test from the batch? This action cannot be undone.')) {
      try {
        const res = await axios.delete(`${API_BASE_URL}/batch-test/${id}`); // Correct endpoint for deleting batch test
        console.log('Delete response:', res);
        // Remove the deleted test from the list
        setBatchTests(batchTests.filter(test => test.id !== id));
        
      } catch (error) {
        console.error('Error removing test:', error);
        if (error.response) {
          alert(`Failed to remove test: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
        } else {
          alert('Failed to remove test: ' + error.message);
        }
      }
    }
  };

  const filteredTests = folderTests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(testSearchQuery.toLowerCase());
    const matchesType = typeFilter === '' || (test.types && test.types.includes(typeFilter));
    return matchesSearch && matchesType;
  });

  // View test
  const handleViewTest = async (testId) => {
    navigate(`/tests/${testId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Batch" title="Batch Tests" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading tests...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Batch" title="Batch Tests" />
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
        <button 
          onClick={handleGoBack}
          className="mt-4 px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
          style={{ backgroundColor: currentColor }}
        >
          <FiArrowLeft /> Back to Batch
        </button>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <button 
              onClick={handleGoBack}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
            >
              <FiArrowLeft className="mr-2" /> Back to Batch
            </button>
            
            <Header title="Batch Tests" />
            
            {batch && (
              <div className="mt-2 text-gray-600">
                <span>Batch: {batch.batch_name}</span>
              </div>
            )}
          </div>
          
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
            style={{ backgroundColor: currentColor }}
          >
            <FiPlus /> Assign Test
          </button>
        </div>
      </div>
      
      {/* Tests list */}
      <div className="mt-6">
        <h3 className="text-lg font-medium mb-4">Assigned Tests ({batchTests.length})</h3>
        
        {batchTests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S No
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name of Test
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Date 
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Window
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...batchTests]
                  .sort((a, b) => {
                    const dateA = a.assigned_on || a.createdAt || a.created_on || a.updated_at;
                    const dateB = b.assigned_on || b.createdAt || b.created_on || b.updated_at;
                    const timeA = dateA ? new Date(dateA).getTime() : 0;
                    const timeB = dateB ? new Date(dateB).getTime() : 0;
                    if (timeB !== timeA) return timeB - timeA; // latest first
                    // fallback to id desc
                    return (b.id || 0) - (a.id || 0);
                  })
                  .map((test, index) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{test.title}</div>
                      <div className="text-sm text-gray-500">Test ID: {test.test_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {test.subject ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {test.subject}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">Not specified</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(test.assigned_on || test.createdAt || test.created_on || test.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col gap-1">
                        <div>{getValidityBadge(test)}</div>
                        {(test.start_time || test.end_time) && (
                          <span className="text-xs text-gray-500">
                            {test.start_time ? new Date(test.start_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Anytime'}
                            {' → '}
                            {test.end_time ? new Date(test.end_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'No end'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewTest(test.test_id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Test"
                        >
                          <FiFileText className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/batch-test-results/${batchId}/${test.id}`)}
                          className="text-green-600 hover:text-green-900"
                          title="View Results"
                        >
                          <FiBarChart2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(test)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Test"
                        >
                          <FiEdit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTest(test.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Remove Test"
                        >
                          <FiTrash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-gray-200 text-gray-400">
                <FiCheckSquare size={24} />
              </div>
            </div>
            <p className="text-gray-600 mb-4">No tests have been assigned to this batch yet.</p>
            <button
              onClick={handleOpenAddModal}
              className="px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
              style={{ backgroundColor: currentColor }}
            >
              <FiPlus /> Assign Your First Test
            </button>
          </div>
        )}
      </div>
      
      {/* Add Test Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-[95%] max-w-[1450px]">
              <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-xl leading-6 font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100">
                      Assign Test to Batch
                    </h3>
                    
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left: Folder Browser */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-[500px] overflow-y-auto">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Browse Directory</h4>
                        <FolderBrowser
                          initialFolderId={null}
                          onFolderChange={handleFolderChange}
                          showActions={false}
                        />
                      </div>
                      
                      {/* Right: Tests Selection & Details */}
                      <div className="flex flex-col">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Available Tests</h4>
                        
                        {selectedFolderId ? (
                          <div className="flex-1 flex flex-col min-h-0">
                            {/* Filters row */}
                            <div className="flex gap-2 mb-3">
                              <input
                                type="text"
                                value={testSearchQuery}
                                onChange={(e) => {
                                  setTestSearchQuery(e.target.value);
                                  setFocusedIndex(-1);
                                }}
                                className="block w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                placeholder="Search test by name..."
                              />
                              
                              <select
                                value={typeFilter}
                                onChange={(e) => {
                                  setTypeFilter(e.target.value);
                                  setFocusedIndex(-1);
                                }}
                                className="block w-40 px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                              >
                                <option value="">All Types</option>
                                <option value="WEEKLY_TEST">Weekly Test</option>
                                <option value="MOCK_TEST">Mock Test</option>
                                <option value="COURSE_TEST">Course Test</option>
                                <option value="TEST_SERIES">Test Series</option>
                                <option value="TEST_OF_THE_DAY">Test of the Day</option>
                                <option value="SCHOLARSHIP_TEST">Scholarship Test</option>
                              </select>
                            </div>

                            {testsLoading ? (
                              <div className="py-12 text-center text-sm text-gray-500">
                                <span className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></span>
                                Loading tests...
                              </div>
                            ) : filteredTests.length > 0 ? (
                              <div 
                                onKeyDown={(e) => handleKeyDown(e, filteredTests)}
                                tabIndex={0}
                                className="max-h-[220px] overflow-y-auto overflow-x-auto border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                              >
                                <table className="min-w-full divide-y divide-gray-200 text-xs">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xxs font-semibold text-gray-500 uppercase">Select</th>
                                      <th className="px-3 py-2 text-left text-xxs font-semibold text-gray-500 uppercase">Test Name</th>
                                      <th className="px-3 py-2 text-left text-xxs font-semibold text-gray-500 uppercase">Duration</th>
                                      <th className="px-3 py-2 text-left text-xxs font-semibold text-gray-500 uppercase">Types</th>
                                      <th className="px-3 py-2 text-left text-xxs font-semibold text-gray-500 uppercase">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-100">
                                    {filteredTests.map((test, index) => {
                                      const isSelected = selectedTest === test.test_id;
                                      const isFocused = focusedIndex === index;
                                      return (
                                        <tr
                                          key={test.test_id}
                                          onClick={() => {
                                            setSelectedTest(test.test_id);
                                            setTestTitle(test.name);
                                            setFocusedIndex(index);
                                          }}
                                          className={`cursor-pointer transition-colors ${
                                            isSelected ? 'bg-blue-50' : isFocused ? 'bg-gray-100' : 'hover:bg-gray-50'
                                          }`}
                                        >
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            <input
                                              type="radio"
                                              name="modalSelectedTest"
                                              checked={isSelected}
                                              onChange={() => {}}
                                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                          </td>
                                          <td className="px-3 py-2 font-medium text-gray-900">{test.name}</td>
                                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{test.time_duration} mins</td>
                                          <td className="px-3 py-2">
                                            {test.types && test.types.map(t => (
                                              <span key={t} className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-800 mr-1 whitespace-nowrap mb-0.5">
                                                {t}
                                              </span>
                                            ))}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                              test.publish_status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                              {test.publish_status ? 'Published' : 'Draft'}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="py-12 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-md mb-4">
                                <p className="font-semibold text-gray-700">No tests found in this folder</p>
                                <p className="text-xs text-gray-500 mt-1">Create a new test or browse a different directory.</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="py-12 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg mb-4">
                            Select a folder from the directory browser to view available tests.
                          </div>
                        )}

                        {/* Title & Subject Inputs */}
                        <div className="border-t border-gray-100 pt-4 mt-auto">
                          <div className="mb-3">
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Assigned Test Title
                            </label>
                            <input
                              type="text"
                              value={testTitle}
                              onChange={(e) => setTestTitle(e.target.value)}
                              className="mt-1 block w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Enter title for this batch assignment"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Subject
                            </label>
                            <select
                              value={testSubject}
                              onChange={(e) => setTestSubject(e.target.value)}
                              className="mt-1 block w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                              <option value="">-- Select a subject --</option>
                              {commonSubjects.map((subject) => (
                                <option key={subject} value={subject}>
                                  {subject}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Time Window (Optional) */}
                          <div className="grid grid-cols-2 gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200 mb-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Start Time (Optional)
                              </label>
                              <input
                                type="datetime-local"
                                value={testStartTime}
                                onChange={(e) => setTestStartTime(e.target.value)}
                                className="w-full px-2.5 py-1 bg-white border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                End Time (Optional)
                              </label>
                              <input
                                type="datetime-local"
                                value={testEndTime}
                                onChange={(e) => setTestEndTime(e.target.value)}
                                className="w-full px-2.5 py-1 bg-white border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="col-span-2 text-[11px] text-gray-500">
                              Leave both empty for unrestricted access.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 sm:flex sm:flex-row-reverse border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleAssignTest}
                  disabled={!selectedTest}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    selectedTest ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed opacity-50'
                  }`}
                  style={{ backgroundColor: selectedTest ? currentColor : '#9CA3AF' }}
                >
                  Assign Test
                </button>
                <button
                  type="button"
                  onClick={handleCloseAddModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Test Modal */}
      {isEditModalOpen && currentEditTest && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Edit Test Details
                    </h3>
                    
                    <div className="mt-2">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Test ID
                        </label>
                        <input
                          type="text"
                          value={currentEditTest.test_id}
                          disabled
                          className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm sm:text-sm cursor-not-allowed"
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Test Title
                        </label>
                        <input
                          type="text"
                          value={testTitle}
                          onChange={(e) => setTestTitle(e.target.value)}
                          className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Enter a title for this test"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject
                        </label>
                        <div className="relative">
                          <select
                            value={testSubject}
                            onChange={(e) => setTestSubject(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">-- Select a subject --</option>
                            {commonSubjects.map((subject) => (
                              <option key={subject} value={subject}>
                                {subject}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Time Window (Optional) */}
                      <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Start Time (Optional)
                          </label>
                          <input
                            type="datetime-local"
                            value={testStartTime}
                            onChange={(e) => setTestStartTime(e.target.value)}
                            className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            End Time (Optional)
                          </label>
                          <input
                            type="datetime-local"
                            value={testEndTime}
                            onChange={(e) => setTestEndTime(e.target.value)}
                            className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="col-span-2 text-xs text-gray-500">
                          Leave both empty for unrestricted access.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpdateTest}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm"
                  style={{ backgroundColor: currentColor }}
                >
                  Update Test
                </button>
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchTest;